import React, { forwardRef, Suspense, useRef } from "react"
import { faShieldCheck } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useNavigate, useParams } from "react-router-dom"
import { enqueueSnackbar } from "notistack"
import { FormikProps } from "formik"
import { EditFeatureForm } from "../components/EditFeatureForm"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import {
    editFeature,
    fetchSupportFeatureDetails,
    FetchSupportFeatureDetailsResponse,
    SupportFeaturesQuery,
} from "../featuresApi"
import { SUPPORT_ADMIN_ROUTE, SUPPORT_FEATURES_ROUTE } from "../../../../../constants/routes"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { Button } from "../../../../office/admin/components/Button"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { EditFeatureFormValues, FeatureStatus } from "../featuresTypes"

export function EditFeature() {
    const featureFormRef = useRef<FormikProps<EditFeatureFormValues>>(null)

    const handleSaveClick = () => {
        if (featureFormRef.current) {
            featureFormRef.current?.submitForm()
        }
    }

    return (
        <>
            <LocalizedStrict id="support-admin-feature-edit-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPORT_ADMIN_FEATURES_EDIT}
                    title="Edit feature"
                    icon={<FontAwesomeIcon icon={faShieldCheck} />}
                    rightElement={
                        <LocalizedStrict id="support-admin-feature-edit-save-button">
                            <Button onClick={handleSaveClick}>Save</Button>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="support-nav-item-home">
                            <BreadcrumbLink to={SUPPORT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-nav-item-features">
                            <BreadcrumbLink to={SUPPORT_FEATURES_ROUTE}>Feature</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-admin-feature-edit-nav-item-feature">
                            <Breadcrumb>Edit Feature</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Feature ref={featureFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

const selectFeatureDetails = (data: FetchSupportFeatureDetailsResponse): EditFeatureFormValues | null => {
    const feature = data?.feature
    if (!feature) return null

    return {
        progId: feature.progId,
        name: feature.name,
        plan: feature.plan,
        status: feature?.status || FeatureStatus.NEW,
        isEnabled: feature.isEnabled,
    }
}

const Feature = forwardRef<FormikProps<EditFeatureFormValues>>((props, ref) => {
    const navigate = useNavigate()
    const { featureId } = useParams()

    const { data } = useApiQuery(fetchSupportFeatureDetails, {
        queryName: SupportFeaturesQuery.DETAILS,
        dependencies: {
            featureId: featureId || "",
        },
        enabled: Boolean(featureId),
        select: selectFeatureDetails,
        suspense: true,
        useErrorBoundary: true,
    })

    const { mutate: callEditFeature } = useApiMutation(editFeature, {
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [SupportFeaturesQuery.LIST] })
            if ("serviceResponse" in data && data.serviceResponse.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse.displayMessage,
                })
            }
            navigate(SUPPORT_FEATURES_ROUTE)
        },
    })

    const onEditFeature = (data: EditFeatureFormValues) => {
        if (!featureId) return

        callEditFeature({
            featureId: featureId,
            feature: data,
        })
    }

    return <EditFeatureForm ref={ref} initialValues={data} onSubmit={onEditFeature} />
})
